import { theme } from 'antd';
import type React from 'react';
import { TRANSLATIONS } from '../constants/translations';

/**
 * Shared color/radius tokens for the "creamy/smooth" visual language already
 * established in Settings, the filters modal and the toast system -- reused
 * here instead of introducing a parallel palette. TEAL matches
 * FILTER_ACCENT_COLOR (filterSliderTheme.ts) and SURFACE_1 matches
 * FILTER_INACTIVE_FILL, kept as separate constants since callers here (booking
 * calendar/timeline) have nothing to do with the filters modal.
 */
export const TEAL = '#1D9E75';
export const TEAL_LIGHT = '#5DCAA5';
export const TEAL_SOFT_BG = '#E7F5EF';
export const SURFACE_1 = '#F2ECE3';
// A diluted "café con leche" version of SURFACE_1 -- exact value picked by
// hand (rgb(252,247,240)) -- for panels like the booking payment summary
// and the hourly timeline bar's free segments, where full-strength
// SURFACE_1 read as too strong/saturated a fill for a large area, while
// still wanting some warmth instead of plain white.
export const SURFACE_1_SOFT = '#FCF7F0';

/**
 * Coral -- the app's brand/primary-action color, introduced with the mobile
 * home redesign and meant to be reused by every other screen that follows it
 * (see that PR's own notes). CORAL is the solid tone for primary CTAs and
 * brand marks (mobile header logo, bottom nav's central "+"); the other
 * three are progressively lighter tints for soft backgrounds/hover states,
 * darkest to lightest: CORAL_TINT_DARK, CORAL_TINT_LIGHT, CORAL_SOFT_BG.
 */
export const CORAL = '#D85A30';
export const CORAL_TINT_DARK = '#F0997B';
export const CORAL_TINT_LIGHT = '#F5C4B3';
export const CORAL_SOFT_BG = '#FAECE7';

// Coral at low alpha, for the ring that pulses around a row you were sent to
// from a notification (see IncomingBookings' highlight). Kept as tokens
// rather than inline rgba() so the halo can't drift from CORAL itself.
export const CORAL_RING = 'rgba(216, 90, 48, 0.22)';
export const CORAL_RING_CLEAR = 'rgba(216, 90, 48, 0)';

// "Latte" warm neutral -- a step warmer than SURFACE_1, used as the filled
// background behind grouped search/filter chrome (mobile Explorar's search
// card and its Filters pill) so they read as one family distinct from the
// plain white fields/cards sitting on top of it. Reuse this instead of
// hardcoding the hex on future screens that want the same accent.
export const SURFACE_WARM = '#F3E8DC';

// Dark panel tones for the publish wizards' left-hand illustration panel --
// one per phase, purely a visual cue that this is a different chapter, no
// other meaning attached.
//
// They used to be a set of "jewel tones" inherited from the spa app --
// navy, indigo, plum -- which put a cold blue next to a screen whose every
// other surface is cream, teal and coral. These are built from the app's
// own two brand hues instead: TEAL above walked round to pine, olive and
// deep teal on one side, CORAL walked to ochre, terracotta and warm taupe
// on the other. Nothing blue, nothing purple.
//
// Each is a real hue at low lightness rather than a darkened mix, so they
// keep their colour instead of sliding towards black, and every one clears
// 8.5:1 against the white text the panel puts on top -- comfortably past
// WCAG AA, which matters here because the panel carries the step's title
// and subtitle, not just decoration.
//
// Wizards alternate warm and cool between consecutive steps (see each
// wizard's own PHASES list). Seven near-identical greens in a row would
// tell a host nothing about having moved on.
export const WIZARD_TONE_EMERALD = '#184e3e';
export const WIZARD_TONE_OCHRE = '#4e3c1d';
export const WIZARD_TONE_PINE = '#19482d';
export const WIZARD_TONE_TERRACOTTA = '#54291c';
export const WIZARD_TONE_OLIVE = '#3b4422';
export const WIZARD_TONE_TAUPE = '#423329';
export const WIZARD_TONE_DEEP_TEAL = '#23524e';

// Diagonal-stripe patterns for the hourly timeline: coral for an actual
// booking, neutral gray for the cleanup buffer around it -- same stripe
// geometry, different colors, so the two read as related but distinct.
export const BOOKED_STRIPES = 'repeating-linear-gradient(45deg, #FBDCD4, #FBDCD4 6px, #F6C4B8 6px, #F6C4B8 12px)';
export const BOOKED_BORDER = '#E2896F';
export const BUFFER_STRIPES = 'repeating-linear-gradient(45deg, #EFE9DF, #EFE9DF 6px, #E3DCCF 6px, #E3DCCF 12px)';
// Cool gray, same stripe geometry as the other two -- deliberately neither
// warm (BUFFER_STRIPES) nor coral (BOOKED_STRIPES) so "not bookable yet
// because it's too soon" reads as its own distinct reason, not a variant of
// either "someone else booked this" or "cleaning time".
export const TOO_SOON_STRIPES = 'repeating-linear-gradient(45deg, #ECECEC, #ECECEC 6px, #E0E0E0 6px, #E0E0E0 12px)';

export type PillTone = 'default' | 'success' | 'warning' | 'error';

/** A filled, fully-rounded badge style driven by antd's own semantic tokens
 * (so it follows the app's theme instead of hardcoded hex), matching the
 * pill badges already used on HeavyDutyMachineryCard/Settings/toasts. */
export const pillToneStyle = (
  tone: PillTone,
  token: ReturnType<typeof theme.useToken>['token'],
): React.CSSProperties => {
  const byTone: Record<PillTone, React.CSSProperties> = {
    default: { background: token.colorFillTertiary, color: token.colorTextSecondary },
    success: { background: token.colorSuccessBg, color: token.colorSuccess },
    warning: { background: token.colorWarningBg, color: token.colorWarning },
    error: { background: token.colorErrorBg, color: token.colorError },
  };
  return byTone[tone];
};

export const HEAVY_DUTY_MACHINERY_SPACE_STATUS_TONES: Record<string, PillTone> = {
  Draft: 'default',
  PendingVerification: 'warning',
  Active: 'success',
  PendingImprovement: 'warning',
  Suspended: 'error',
};

/** Translated label for each HeavyDutyMachinery.Status value -- pass through
 * translate(t, ...) at the call site, same as every other label key. */
export const HEAVY_DUTY_MACHINERY_SPACE_STATUS_LABEL_KEYS: Record<string, string> = {
  Draft: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_STATUS_DRAFT,
  PendingVerification: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_STATUS_PENDING_VERIFICATION,
  Active: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_STATUS_ACTIVE,
  PendingImprovement: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_STATUS_PENDING_IMPROVEMENT,
  Suspended: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_STATUS_SUSPENDED,
};

// The aggregate Public-level telemetry status (IPublicMachineryTelemetry.status)
// -- distinct from HEAVY_DUTY_MACHINERY_SPACE_STATUS_TONES above, which is
// about the LISTING's own publish-workflow Status (Draft/Active/Suspended/...),
// not whether the machine is currently rented out.
export const MACHINERY_LIVE_STATUS_TONES: Record<string, PillTone> = {
  Available: 'success',
  Rented: 'warning',
  Maintenance: 'error',
};

export const MACHINERY_LIVE_STATUS_LABEL_KEYS: Record<string, string> = {
  Available: TRANSLATIONS.HEAVY_DUTY_MACHINERY_LIVE_STATUS_AVAILABLE,
  Rented: TRANSLATIONS.HEAVY_DUTY_MACHINERY_LIVE_STATUS_RENTED,
  Maintenance: TRANSLATIONS.HEAVY_DUTY_MACHINERY_LIVE_STATUS_MAINTENANCE,
};
